<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import {
    Button,
    ButtonVariant,
    Checkbox,
    Dropdown,
    dropdownPanelDividerClass,
    DropdownPanelScroll,
    Tooltip,
  } from '@tundr/ds-components';
  import { computed } from 'vue';
  import { useColumnVisibility } from '../../composables/use-column-visibility';
  import type { Column } from '../DataTable/DataTable.types';
  import {
    columnListClass,
    dropdownTitleClass,
    tooltipAnchorClass,
  } from './ColumnVisibilityDropdown.css';

  const props = withDefaults(
    defineProps<{
      columns: Column[];
      namespace: string;
      nonHideableColumns?: string[];
    }>(),
    {
      nonHideableColumns: () => [],
    },
  );

  const { t } = useLocale('table');

  const {
    isColumnHideable,
    isColumnVisible,
    toggleColumn,
    toggleAll,
    areAllVisible,
    areSomeVisible,
  } = useColumnVisibility(props.namespace, props.nonHideableColumns);

  const displayedColumnsWithIndex = computed(() =>
    props.columns
      .map((col, index) => ({ column: col, originalIndex: index }))
      .filter((entry) => entry.column.value !== 'actions'),
  );

  const onToggleAll = () => {
    const allVisible = areAllVisible(props.columns);
    toggleAll(props.columns, !allVisible);
  };

  const onToggleColumn = (column: Column, index: number) => {
    if (!isColumnHideable(column, index)) return;
    toggleColumn(column, index);
  };
</script>

<template>
  <!--
    Tooltip and Dropdown each resolve their own trigger by `data-scope`, so they
    cannot share one element: nesting both through `as-child` makes the dropdown
    overwrite the tooltip and leaves it without an anchor to position against.
  -->
  <Tooltip :title="t('column_visibility.button_label')">
    <span :class="tooltipAnchorClass">
      <Dropdown id="column-visibility" fixed-height trigger-as-child>
        <template #trigger>
          <Button
            :variant="ButtonVariant.Icon"
            :icon-name="TUNDR_ICONS.MANAGE_COLUMNS"
            :aria-label="t('column_visibility.button_label')"
            test-id="column-visibility-trigger"
          />
        </template>

        <div :class="dropdownTitleClass">
          {{ t('column_visibility.title') }}
        </div>
        <div :class="dropdownPanelDividerClass" />

        <DropdownPanelScroll :class="columnListClass">
          <div data-testid="column-visibility-toggle-all">
            <Checkbox
              id="column-visibility-toggle-all"
              name="column-visibility-toggle-all"
              :label="t('column_visibility.toggle_all')"
              :model-value="areAllVisible(columns)"
              :indeterminate="areSomeVisible(columns)"
              full-width
              @update:model-value="onToggleAll"
            />
          </div>

          <div :class="dropdownPanelDividerClass" />

          <div
            v-for="{ column, originalIndex } in displayedColumnsWithIndex"
            :key="column.value"
            :data-testid="`column-visibility-${column.value}`"
          >
            <Checkbox
              :id="`column-visibility-${column.value}`"
              :name="`column-visibility-${column.value}`"
              :label="column.text"
              :model-value="isColumnVisible(column.value)"
              :disabled="!isColumnHideable(column, originalIndex)"
              full-width
              @update:model-value="() => onToggleColumn(column, originalIndex)"
            />
          </div>
        </DropdownPanelScroll>
      </Dropdown>
    </span>
  </Tooltip>
</template>
